import React, { useState, useRef, useEffect } from 'react'
import * as XLSX from 'xlsx'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../services/api'
import { useAuthStore } from '../store/auth.store'
import { useNavigate } from 'react-router-dom'
import { PageHeader, Card, Button, Field, Input } from '../components/ui'
import { VenueType, PlanType } from '@teffy/shared-types'

interface RestaurantSummary {
  id: string
  slug: string
  name: string
  logoUrl: string | null
  venueType: VenueType
  isActive: boolean
  plan: PlanType
  planExpiresAt: string | null
  serviceChargePct: number
  createdAt: string
  _count: { users: number; orders: number; tables: number }
  selfService: boolean
  contractedDishes: number | null
  skipKitchen: boolean
  hidePrices: boolean
  trialEndedAt: string | null
}

const PLAN_COLOR: Record<PlanType, string> = {
  [PlanType.BASIC]: 'bg-gray-100 text-gray-600',
  [PlanType.PRO]: 'bg-blue-50 text-blue-700',
  [PlanType.ENTERPRISE]: 'bg-purple-50 text-purple-700',
}

const VENUE_LABELS: Record<VenueType, string> = {
  [VenueType.RESTAURANT]: 'Restaurante',
  [VenueType.HOTEL]: 'Hotel',
  [VenueType.APARTMENT]: 'Apartamento',
  [VenueType.HOSPITAL]: 'Hospital',
  [VenueType.EVENT]: 'Salón de eventos',
}

function ActionBtn({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className="px-2.5 py-1 text-xs text-gray-500 hover:text-gray-900 hover:bg-white hover:shadow-sm rounded-md transition-all font-medium"
    >
      {children}
    </button>
  )
}

export default function SuperAdminPage() {
  const [tab, setTab] = useState<'negocios' | 'servicio' | 'grupos'>('negocios')
  const [showCreate, setShowCreate] = useState(false)
  const [planTarget, setPlanTarget] = useState<RestaurantSummary | null>(null)
  const [venueTarget, setVenueTarget] = useState<RestaurantSummary | null>(null)
  const [creditsTarget, setCreditsTarget] = useState<RestaurantSummary | null>(null)
  const [webhookTarget, setWebhookTarget] = useState<RestaurantSummary | null>(null)
  const [paymentTarget, setPaymentTarget] = useState<RestaurantSummary | null>(null)
  const [importTarget, setImportTarget] = useState<RestaurantSummary | null>(null)
  const [endTrialTarget, setEndTrialTarget] = useState<RestaurantSummary | null>(null)
  const [cloneMenuTarget, setCloneMenuTarget] = useState<RestaurantSummary | null>(null)
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const clearAuth = useAuthStore((s) => s.clearAuth)
  const enterImpersonation = useAuthStore((s) => s.enterImpersonation)
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { data: restaurants = [], isLoading } = useQuery<RestaurantSummary[]>({
    queryKey: ['superadmin-restaurants'],
    queryFn: async () => {
      const { data } = await api.get('/superadmin/restaurants')
      return data
    },
  })

  const toggleMutation = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.patch(`/superadmin/restaurants/${id}/toggle`)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['superadmin-restaurants'] }),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/superadmin/restaurants/${id}`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['superadmin-restaurants'] }),
  })

  const enterMutation = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/superadmin/restaurants/${id}/enter`)
      return data as { token: string; user: { id: string; name: string }; restaurant: { id: string; name: string } }
    },
    onSuccess: (data) => {
      qc.clear()
      enterImpersonation({
        token: data.token,
        userId: data.user.id,
        restaurantId: data.restaurant.id,
        restaurantName: data.restaurant.name,
        userName: data.user.name,
      })
      navigate('/dashboard')
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'No se pudo entrar al panel de este negocio'
      alert(msg)
    },
  })

  function handleLogout() {
    clearAuth()
    navigate('/login')
  }

  const active = restaurants.filter((r) => r.isActive).length
  const inactive = restaurants.length - active

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div>
          <span className="text-2xl font-black tracking-widest text-gray-900">TEFFY</span>
          <span className="ml-3 text-xs font-semibold uppercase tracking-widest text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
            Superadmin
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-gray-100 rounded-lg p-1 gap-1">
            <button
              onClick={() => setTab('negocios')}
              className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${
                tab === 'negocios' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Negocios
            </button>
            <button
              onClick={() => setTab('servicio')}
              className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${
                tab === 'servicio' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Servicio
            </button>
            <button
              onClick={() => setTab('grupos')}
              className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${
                tab === 'grupos' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Grupos
            </button>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout}>
            Cerrar sesión
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {tab === 'servicio' ? (
          <ServicePanel />
        ) : tab === 'grupos' ? (
          <GroupsPanel />
        ) : (
        <>
        <PageHeader
          title="Negocios"
          subtitle={`${restaurants.length} registrados · ${active} activos · ${inactive} suspendidos`}
          action={
            <Button onClick={() => setShowCreate(true)}>+ Nuevo negocio</Button>
          }
        />

        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <Card className="p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Total negocios</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{restaurants.length}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Activos</p>
            <p className="text-3xl font-bold text-green-600 mt-1">{active}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Suspendidos</p>
            <p className="text-3xl font-bold text-red-500 mt-1">{inactive}</p>
          </Card>
        </div>

        {/* Restaurant list */}
        {isLoading ? (
          <p className="text-center text-gray-400 py-12">Cargando...</p>
        ) : restaurants.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-gray-400">No hay negocios registrados</p>
            <Button className="mt-4" onClick={() => setShowCreate(true)}>
              Crear primer negocio
            </Button>
          </Card>
        ) : (
          <div className="space-y-3">
            {restaurants.map((r) => (
              <Card key={r.id} className={`overflow-hidden ${!r.isActive ? 'opacity-60' : ''}`}>
                {/* Zona superior: identidad + stats + Entrar */}
                <div className="px-5 pt-4 pb-3 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-2 h-2 rounded-full shrink-0 ${r.isActive ? 'bg-green-500' : 'bg-red-400'}`} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-gray-900">{r.name}</p>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${PLAN_COLOR[r.plan]}`}>
                          {r.plan}
                        </span>
                        {r.planExpiresAt && new Date(r.planExpiresAt) < new Date() && (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-600">
                            VENCIDO
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 truncate">
                        {r.slug} · {VENUE_LABELS[r.venueType]} · {new Date(r.createdAt).toLocaleDateString('es-MX')}
                        {r.planExpiresAt && ` · vence ${new Date(r.planExpiresAt).toLocaleDateString('es-MX')}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-5 shrink-0">
                    <div className="hidden sm:flex items-center gap-3 text-xs text-gray-400">
                      <span>{r._count.users} usuarios</span>
                      <span className="text-gray-200">·</span>
                      <span>{r._count.orders} órdenes</span>
                      <span className="text-gray-200">·</span>
                      <span>{r._count.tables} mesas</span>
                    </div>
                    <Button
                      variant="primary"
                      size="sm"
                      loading={enterMutation.isPending && enterMutation.variables === r.id}
                      onClick={() => enterMutation.mutate(r.id)}
                    >
                      Entrar →
                    </Button>
                  </div>
                </div>

                {/* Zona inferior: acciones de configuración */}
                <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-0.5 flex-wrap">
                    <ActionBtn onClick={() => setImportTarget(r)}>📥 Menú</ActionBtn>
                    <ActionBtn onClick={() => setCloneMenuTarget(r)}>📋 Clonar</ActionBtn>
                    <ActionBtn onClick={() => setVenueTarget(r)}>🏪 Venue</ActionBtn>
                    <ActionBtn onClick={() => setPlanTarget(r)}>💳 Plan</ActionBtn>
                    <ActionBtn onClick={() => setCreditsTarget(r)}>🎉 Eventos</ActionBtn>
                    <ActionBtn onClick={() => setWebhookTarget(r)}>🔗 Webhook</ActionBtn>
                    <ActionBtn onClick={() => setPaymentTarget(r)}>💳 Pasarela</ActionBtn>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => toggleMutation.mutate(r.id)}
                      disabled={toggleMutation.isPending}
                      className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                        r.isActive
                          ? 'text-red-500 hover:bg-red-50'
                          : 'text-green-700 hover:bg-green-50'
                      }`}
                    >
                      {r.isActive ? 'Suspender' : 'Activar'}
                    </button>
                    <div className="relative">
                      <button
                        onClick={() => setOpenMenuId(openMenuId === r.id ? null : r.id)}
                        className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-md transition-colors text-base leading-none pb-0.5"
                        title="Más opciones"
                      >
                        ···
                      </button>
                      {openMenuId === r.id && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)} />
                          <div className="absolute right-0 bottom-full mb-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-40 py-1 overflow-hidden">
                            {!r.trialEndedAt ? (
                              <button
                                onClick={() => { setEndTrialTarget(r); setOpenMenuId(null) }}
                                className="w-full text-left px-4 py-2 text-xs text-red-600 hover:bg-red-50 transition-colors"
                              >
                                Fin prueba
                              </button>
                            ) : (
                              <div className="px-4 py-2 text-xs text-gray-400">
                                Activo desde {new Date(r.trialEndedAt).toLocaleDateString('es-MX')}
                              </div>
                            )}
                            <div className="border-t border-gray-100 my-1" />
                            <button
                              onClick={() => {
                                setOpenMenuId(null)
                                if (confirm(`¿Eliminar "${r.name}"? Esta acción no se puede deshacer.`)) {
                                  deleteMutation.mutate(r.id)
                                }
                              }}
                              className="w-full text-left px-4 py-2 text-xs text-red-600 hover:bg-red-50 transition-colors font-medium"
                            >
                              Eliminar negocio
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
        </>
        )}
      </div>

      {/* Modals (fixed-position, fuera del flujo de contenido) */}
      {showCreate && (
        <CreateRestaurantModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false)
            qc.invalidateQueries({ queryKey: ['superadmin-restaurants'] })
          }}
        />
      )}
      {planTarget && (
        <PlanModal
          restaurant={planTarget}
          onClose={() => setPlanTarget(null)}
          onSaved={() => {
            setPlanTarget(null)
            qc.invalidateQueries({ queryKey: ['superadmin-restaurants'] })
          }}
        />
      )}
      {venueTarget && (
        <VenueModal
          restaurant={venueTarget}
          onClose={() => setVenueTarget(null)}
          onSaved={() => {
            setVenueTarget(null)
            qc.invalidateQueries({ queryKey: ['superadmin-restaurants'] })
          }}
        />
      )}
      {creditsTarget && (
        <EventCreditsModal
          restaurant={creditsTarget}
          onClose={() => setCreditsTarget(null)}
          onSaved={() => {
            setCreditsTarget(null)
            qc.invalidateQueries({ queryKey: ['superadmin-restaurants'] })
          }}
        />
      )}
      {webhookTarget && (
        <WebhookModal
          restaurant={webhookTarget}
          onClose={() => setWebhookTarget(null)}
        />
      )}
      {paymentTarget && (
        <PaymentModal
          restaurant={paymentTarget}
          onClose={() => setPaymentTarget(null)}
        />
      )}
      {importTarget && (
        <MenuImportModal
          restaurant={importTarget}
          onClose={() => setImportTarget(null)}
        />
      )}
      {endTrialTarget && (
        <EndTrialModal
          restaurant={endTrialTarget}
          onClose={() => setEndTrialTarget(null)}
          onDone={() => {
            setEndTrialTarget(null)
            qc.invalidateQueries({ queryKey: ['superadmin-restaurants'] })
          }}
        />
      )}
      {cloneMenuTarget && (
        <CloneMenuModal
          restaurant={cloneMenuTarget}
          allRestaurants={restaurants}
          onClose={() => setCloneMenuTarget(null)}
        />
      )}
    </div>
  )
}

// ─── End Trial Modal ──────────────────────────────────────────────────────────
function EndTrialModal({
  restaurant,
  onClose,
  onDone,
}: {
  restaurant: RestaurantSummary
  onClose: () => void
  onDone: () => void
}) {
  const { mutate, isPending, isSuccess, data } = useMutation({
    mutationFn: async () => {
      const { data } = await api.post(`/superadmin/restaurants/${restaurant.id}/end-trial`)
      return data as { deletedOrders: number; trialEndedAt: string }
    },
  })

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4">
        {isSuccess ? (
          <div className="text-center space-y-3">
            <div className="text-5xl">✅</div>
            <p className="text-lg font-bold text-gray-900">¡Periodo de prueba finalizado!</p>
            <p className="text-sm text-gray-500">
              Se eliminaron <strong>{data.deletedOrders}</strong> órdenes de prueba.
              El historial está limpio y listo para órdenes reales.
            </p>
            <Button className="w-full justify-center" onClick={onDone}>
              Cerrar
            </Button>
          </div>
        ) : (
          <>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Finalizar periodo de prueba</h2>
              <p className="text-sm text-gray-500">{restaurant.name}</p>
            </div>
            <div className="bg-red-50 border border-red-100 rounded-xl p-4 space-y-1">
              <p className="text-sm font-semibold text-red-700">Esta acción es irreversible</p>
              <p className="text-sm text-red-600">
                Se eliminarán <strong>todas las órdenes creadas antes de hoy</strong>.
                El historial quedará limpio para empezar con órdenes reales a partir de este día.
              </p>
            </div>
            <p className="text-xs text-gray-400">
              Solo puede ejecutarse una vez. Después el botón desaparece.
            </p>
            <div className="flex gap-2 pt-2">
              <Button variant="secondary" className="flex-1 justify-center" onClick={onClose} disabled={isPending}>
                Cancelar
              </Button>
              <Button
                variant="danger"
                className="flex-1 justify-center"
                loading={isPending}
                onClick={() => mutate()}
              >
                Confirmar y limpiar
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ─── Clone Menu Modal ─────────────────────────────────────────────────────────
function CloneMenuModal({
  restaurant,
  allRestaurants,
  onClose,
}: {
  restaurant: RestaurantSummary
  allRestaurants: RestaurantSummary[]
  onClose: () => void
}) {
  const [fromId, setFromId] = useState('')
  const [result, setResult] = useState<{ clonedMenus: number; clonedDishes: number; clonedIngredients: number; from: string } | null>(null)
  const [error, setError] = useState('')

  const sources = allRestaurants.filter((r) => r.id !== restaurant.id)

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      const { data } = await api.post(`/superadmin/restaurants/${fromId}/clone-menu/${restaurant.id}`)
      return data as { clonedMenus: number; clonedDishes: number; clonedIngredients: number; from: string }
    },
    onSuccess: (data) => setResult(data),
    onError: (e: { response?: { data?: { message?: string } } }) => {
      setError(e.response?.data?.message ?? 'Error al clonar el menú')
    },
  })

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4">
        {result ? (
          <div className="text-center space-y-3">
            <div className="text-5xl">✅</div>
            <p className="text-lg font-bold text-gray-900">¡Menú clonado exitosamente!</p>
            <p className="text-sm text-gray-500">
              Desde <strong>{result.from}</strong> hacia <strong>{restaurant.name}</strong>
            </p>
            <div className="flex justify-center gap-6 text-sm text-gray-600 bg-gray-50 rounded-xl p-3">
              <span><strong>{result.clonedMenus}</strong> menú(s)</span>
              <span><strong>{result.clonedDishes}</strong> platillo(s)</span>
              <span><strong>{result.clonedIngredients}</strong> ingrediente(s)</span>
            </div>
            <Button className="w-full justify-center" onClick={onClose}>Cerrar</Button>
          </div>
        ) : (
          <>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Clonar menú</h2>
              <p className="text-sm text-gray-500">Destino: <strong>{restaurant.name}</strong></p>
            </div>

            <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 space-y-1">
              <p className="text-sm font-semibold text-amber-800">Operación de una sola vez</p>
              <p className="text-xs text-amber-700">
                Solo puede ejecutarse si el restaurante destino no tiene menús. Se copiarán menús, platillos, extras, ingredientes, opciones, combos y horarios con el nuevo restaurantId.
              </p>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                Restaurante origen
              </label>
              <select
                value={fromId}
                onChange={(e) => { setFromId(e.target.value); setError('') }}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="">Selecciona el origen…</option>
                {sources
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
              </select>
            </div>

            {error && <p className="text-sm text-red-500">{error}</p>}

            <div className="flex gap-2 pt-2">
              <Button variant="secondary" className="flex-1 justify-center" onClick={onClose} disabled={isPending}>
                Cancelar
              </Button>
              <Button
                className="flex-1 justify-center"
                loading={isPending}
                disabled={!fromId}
                onClick={() => mutate()}
              >
                Clonar menú
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ─── Menu Import Modal ────────────────────────────────────────────────────────

interface ImportRow {
  menu: string
  nombre: string
  descripcion: string
  precio: number
  categoria: string
  ingredientes: string
  extras: string
  etiquetas: string
}

interface ParsedDish {
  name: string
  nameEn: string
  description: string
  descriptionEn: string
  imageUrl: string
  basePrice: number
  category: string
  tags: string[]
  ingredients: string[]
  extras: { name: string; cost: number }[]
}

interface ParsedMenu {
  name: string
  dishes: ParsedDish[]
}

const VALID_TAGS = ['VEGAN', 'VEGETARIAN', 'GLUTEN_FREE', 'SPICY', 'POPULAR']
const VALID_CATEGORIES = ['MAIN', 'APPETIZER', 'DESSERT', 'DRINK', 'SIDE']

function parseExcelRows(rows: Record<string, unknown>[]): ParsedMenu[] {
  const menuMap = new Map<string, ParsedDish[]>()

  for (const row of rows) {
    const menuName = String(row['menu'] ?? '').trim()
    const nombre = String(row['nombre'] ?? '').trim()
    const precio = Number(row['precio'] ?? 0)

    if (!menuName || !nombre || !precio) continue

    const rawCat = String(row['categoria'] ?? '').trim().toUpperCase()
    const category = VALID_CATEGORIES.includes(rawCat) ? rawCat : 'MAIN'

    const tags = String(row['etiquetas'] ?? '').split(',')
      .map((t) => t.trim().toUpperCase())
      .filter((t) => VALID_TAGS.includes(t))

    const ingredients = String(row['ingredientes'] ?? '').split(',')
      .map((i) => i.trim())
      .filter(Boolean)

    const extras = String(row['extras'] ?? '').split(',')
      .map((e) => e.trim())
      .filter(Boolean)
      .map((e) => {
        const [name, costStr] = e.split(':')
        return { name: name.trim(), cost: Number(costStr ?? 0) }
      })
      .filter((e) => e.name)

    const dish: ParsedDish = {
      name: nombre,
      nameEn: String(row['nombre_en'] ?? '').trim(),
      description: String(row['descripcion'] ?? '').trim(),
      descriptionEn: String(row['descripcion_en'] ?? '').trim(),
      imageUrl: String(row['imagen'] ?? '').trim(),
      basePrice: precio,
      category,
      tags,
      ingredients,
      extras,
    }

    if (!menuMap.has(menuName)) menuMap.set(menuName, [])
    menuMap.get(menuName)!.push(dish)
  }

  return Array.from(menuMap.entries()).map(([name, dishes]) => ({ name, dishes }))
}

function downloadTemplate() {
  const headers = ['menu', 'nombre', 'nombre_en', 'descripcion', 'descripcion_en', 'precio', 'categoria', 'imagen', 'ingredientes', 'extras', 'etiquetas']
  const example = [
    'Comida', 'Hamburguesa Clásica', 'Classic Burger', 'Con lechuga, tomate y queso', 'With lettuce, tomato and cheese', 89, 'MAIN', '', 'Carne,Queso,Lechuga,Tomate', 'Tocino:15,Huevo:10', 'POPULAR',
  ]
  const example2 = [
    'Comida', 'Ensalada Verde', 'Green Salad', 'Fresca y ligera', 'Fresh and light', 65, 'MAIN', '', 'Lechuga,Tomate,Pepino', '', 'VEGAN,GLUTEN_FREE',
  ]
  const example3 = [
    'Bebidas', 'Limonada Natural', 'Fresh Lemonade', 'Recién exprimida', 'Freshly squeezed', 35, 'DRINK', '', '', '', '',
  ]
  const ws = XLSX.utils.aoa_to_sheet([headers, example, example2, example3])
  ws['!cols'] = headers.map((h) => ({ wch: ['descripcion', 'descripcion_en', 'ingredientes', 'imagen'].includes(h) ? 30 : 18 }))
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Menú')
  XLSX.writeFile(wb, 'template_menu_teffy.xlsx')
}

function MenuImportModal({ restaurant, onClose }: { restaurant: RestaurantSummary; onClose: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [parsedMenus, setParsedMenus] = useState<ParsedMenu[]>([])
  const [fileName, setFileName] = useState('')
  const [error, setError] = useState('')
  const [result, setResult] = useState<{ menusCreated: number; dishesCreated: number; ingredientsCreated: number } | null>(null)

  const { mutate: importMenus, isPending } = useMutation({
    mutationFn: async () => {
      const { data } = await api.post(`/superadmin/restaurants/${restaurant.id}/import-menu`, {
        menus: parsedMenus.map((m) => ({
          name: m.name,
          dishes: m.dishes.map((d) => ({
            name: d.name,
            nameEn: d.nameEn || undefined,
            description: d.description,
            descriptionEn: d.descriptionEn || undefined,
            imageUrl: d.imageUrl || undefined,
            basePrice: d.basePrice,
            category: d.category,
            tags: d.tags,
            ingredients: d.ingredients,
            extras: d.extras,
          })),
        })),
      })
      return data
    },
    onSuccess: (data) => setResult(data),
    onError: () => setError('Error al importar. Revisa el formato e intenta de nuevo.'),
  })

  function handleFile(file: File) {
    setError('')
    setParsedMenus([])
    setResult(null)
    setFileName(file.name)
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target?.result, { type: 'array' })
        const ws = wb.Sheets[wb.SheetNames[0]]
        const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: '' })
        const menus = parseExcelRows(rows)
        if (menus.length === 0) {
          setError('No se encontraron filas válidas. Revisa que el archivo tenga las columnas correctas.')
          return
        }
        setParsedMenus(menus)
      } catch {
        setError('No se pudo leer el archivo. Asegúrate de que sea un .xlsx válido.')
      }
    }
    reader.readAsArrayBuffer(file)
  }

  const totalDishes = parsedMenus.reduce((acc, m) => acc + m.dishes.length, 0)

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Importar menú desde Excel</h2>
            <p className="text-sm text-gray-500">{restaurant.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
          {result ? (
            <div className="text-center py-8 space-y-3">
              <div className="text-5xl">✅</div>
              <p className="text-lg font-bold text-gray-900">¡Importación exitosa!</p>
              <div className="flex justify-center gap-6 text-sm text-gray-600">
                <span><strong>{result.menusCreated}</strong> menú(s)</span>
                <span><strong>{result.dishesCreated}</strong> platillo(s)</span>
                <span><strong>{result.ingredientsCreated}</strong> ingrediente(s) creado(s)</span>
              </div>
              <button onClick={onClose} className="mt-4 px-6 py-2 bg-gray-900 text-white rounded-lg font-semibold text-sm">
                Cerrar
              </button>
            </div>
          ) : (
            <>
              {/* Paso 1: Descargar template */}
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm font-semibold text-gray-700 mb-2">1. Descarga el template</p>
                <button
                  onClick={downloadTemplate}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-600 hover:text-indigo-800 underline underline-offset-2"
                >
                  📄 Descargar template_menu_teffy.xlsx
                </button>
                <p className="text-xs text-gray-400 mt-1">
                  Columnas: menu · nombre · nombre_en · descripcion · descripcion_en · precio · categoria · imagen · ingredientes · extras · etiquetas
                </p>
                <p className="text-xs text-gray-400">
                  Categorías válidas: MAIN, APPETIZER, DESSERT, DRINK, SIDE · Etiquetas: POPULAR, VEGAN, VEGETARIAN, GLUTEN_FREE, SPICY
                </p>
              </div>

              {/* Paso 2: Subir archivo */}
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">2. Sube el archivo completado</p>
                <div
                  className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center cursor-pointer hover:border-indigo-300 transition-colors"
                  onClick={() => fileRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFile(f) }}
                >
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".xlsx,.xls"
                    className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f) }}
                  />
                  {fileName ? (
                    <p className="text-sm font-semibold text-gray-700">📎 {fileName}</p>
                  ) : (
                    <>
                      <p className="text-2xl mb-1">📂</p>
                      <p className="text-sm text-gray-500">Arrastra el archivo aquí o haz clic para seleccionar</p>
                      <p className="text-xs text-gray-400 mt-1">.xlsx · .xls</p>
                    </>
                  )}
                </div>
                {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
              </div>

              {/* Paso 3: Preview */}
              {parsedMenus.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">
                    3. Preview — {parsedMenus.length} menú(s) · {totalDishes} platillo(s)
                  </p>
                  <div className="border border-gray-100 rounded-xl overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-gray-500 font-semibold">Menú</th>
                          <th className="px-3 py-2 text-left text-gray-500 font-semibold">Platillo</th>
                          <th className="px-3 py-2 text-left text-gray-500 font-semibold">EN</th>
                          <th className="px-3 py-2 text-right text-gray-500 font-semibold">Precio</th>
                          <th className="px-3 py-2 text-left text-gray-500 font-semibold">Imagen</th>
                          <th className="px-3 py-2 text-left text-gray-500 font-semibold">Ingredientes</th>
                          <th className="px-3 py-2 text-left text-gray-500 font-semibold">Etiquetas</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsedMenus.flatMap((m) =>
                          m.dishes.map((d, i) => (
                            <tr key={`${m.name}-${i}`} className="border-t border-gray-50">
                              <td className="px-3 py-1.5 text-gray-500">{i === 0 ? m.name : ''}</td>
                              <td className="px-3 py-1.5 font-medium text-gray-800">{d.name}</td>
                              <td className="px-3 py-1.5 text-gray-400 italic">{d.nameEn || '—'}</td>
                              <td className="px-3 py-1.5 text-right text-gray-700">${d.basePrice.toFixed(2)}</td>
                              <td className="px-3 py-1.5 text-gray-500">
                                {d.imageUrl ? <span className="text-green-600">✓</span> : '—'}
                              </td>
                              <td className="px-3 py-1.5 text-gray-500">{d.ingredients.join(', ') || '—'}</td>
                              <td className="px-3 py-1.5 text-gray-500">{d.tags.join(', ') || '—'}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!result && (
          <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
            <Button variant="secondary" onClick={onClose}>Cancelar</Button>
            <Button
              disabled={parsedMenus.length === 0 || isPending}
              loading={isPending}
              onClick={() => importMenus()}
            >
              Importar {totalDishes > 0 ? `${totalDishes} platillos` : ''}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Service Panel ────────────────────────────────────────────────────────────

interface PreviewRow {
  restaurantId: string
  restaurantName: string
  orderCount: number
  totalRevenue: number
  totalServiceCharge: number
  billedAmount: string
  note: string
}

interface ServiceRecord {
  id: string
  restaurantId: string
  restaurant: { name: string }
  fromDate: string
  toDate: string
  orderCount: number
  totalRevenue: string
  totalServiceCharge: string
  billedAmount: string | null
  note: string | null
  createdAt: string
}

function fmt(n: number) {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n)
}

function ServicePanel() {
  const qc = useQueryClient()
  const today = new Date().toISOString().slice(0, 10)
  const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10)

  const [restaurantId, setRestaurantId] = useState<string>('')
  const [fromDate, setFromDate] = useState(firstOfMonth)
  const [toDate, setToDate] = useState(today)
  const [rows, setRows] = useState<PreviewRow[]>([])
  const [previewed, setPreviewed] = useState(false)

  const { data: restaurants = [] } = useQuery<RestaurantSummary[]>({
    queryKey: ['superadmin-restaurants'],
    queryFn: async () => (await api.get('/superadmin/restaurants')).data,
    staleTime: 5 * 60 * 1000,
  })

  const { data: history = [], isLoading: historyLoading } = useQuery<ServiceRecord[]>({
    queryKey: ['service-records', restaurantId],
    queryFn: async () => {
      const params = restaurantId ? `?restaurantId=${restaurantId}` : ''
      return (await api.get(`/superadmin/service/records${params}`)).data
    },
  })

  const { mutate: calcular, isPending: calculating } = useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/superadmin/service/preview', {
        fromDate,
        toDate,
        ...(restaurantId ? { restaurantId } : {}),
      })
      return data as Omit<PreviewRow, 'billedAmount' | 'note'>[]
    },
    onSuccess: (data) => {
      setRows(data.map((r) => ({ ...r, billedAmount: '', note: '' })))
      setPreviewed(true)
    },
  })

  const { mutate: guardar, isPending: saving } = useMutation({
    mutationFn: async () => {
      await api.post('/superadmin/service/records', {
        fromDate,
        toDate,
        records: rows.map((r) => ({
          restaurantId: r.restaurantId,
          orderCount: r.orderCount,
          totalRevenue: r.totalRevenue,
          totalServiceCharge: r.totalServiceCharge,
          billedAmount: r.billedAmount ? Number(r.billedAmount) : null,
          note: r.note || null,
        })),
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['service-records'] })
      setRows([])
      setPreviewed(false)
      alert('Corte guardado correctamente.')
    },
  })

  function updateRow(idx: number, field: 'billedAmount' | 'note', value: string) {
    setRows((prev) => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r))
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Servicio" subtitle="Calcula y registra los cargos por uso de TEFFY por negocio" />

      {/* Calculadora */}
      <Card className="p-5 space-y-4">
        <p className="text-sm font-semibold text-gray-700">Filtros</p>
        <div>
          <label className="text-xs text-gray-500 mb-1 block">Negocio</label>
          <select
            value={restaurantId}
            onChange={(e) => { setRestaurantId(e.target.value); setPreviewed(false) }}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
          >
            <option value="">Todos los negocios</option>
            {restaurants
              .filter((r) => r.isActive)
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
          </select>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <label className="text-xs text-gray-500 mb-1 block">Desde</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => { setFromDate(e.target.value); setPreviewed(false) }}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          </div>
          <div className="flex-1">
            <label className="text-xs text-gray-500 mb-1 block">Hasta</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => { setToDate(e.target.value); setPreviewed(false) }}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          </div>
          <div className="pt-5">
            <Button onClick={() => calcular()} loading={calculating} disabled={!fromDate || !toDate}>
              Calcular
            </Button>
          </div>
        </div>

        {/* Tabla de preview */}
        {previewed && rows.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-4">Sin órdenes pagadas en ese rango (fuera de eventos).</p>
        )}

        {previewed && rows.length > 0 && (
          <div className="space-y-3">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-2 text-xs text-gray-400 font-semibold uppercase tracking-wide">Negocio</th>
                    <th className="text-right py-2 text-xs text-gray-400 font-semibold uppercase tracking-wide w-20">Órdenes</th>
                    <th className="text-right py-2 text-xs text-gray-400 font-semibold uppercase tracking-wide w-32">Total facturado</th>
                    <th className="text-right py-2 text-xs text-gray-400 font-semibold uppercase tracking-wide w-36">Cargo por servicio</th>
                    <th className="text-right py-2 text-xs text-gray-400 font-semibold uppercase tracking-wide w-16">%</th>
                    <th className="text-right py-2 text-xs text-gray-400 font-semibold uppercase tracking-wide w-36">A facturar por TEFFY</th>
                    <th className="py-2 text-xs text-gray-400 font-semibold uppercase tracking-wide w-48 pl-3">Nota</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {rows.map((row, idx) => (
                    <tr key={row.restaurantId}>
                      <td className="py-2 font-medium text-gray-900">{row.restaurantName}</td>
                      <td className="py-2 text-right text-gray-600">{row.orderCount}</td>
                      <td className="py-2 text-right text-gray-700">{fmt(row.totalRevenue)}</td>
                      <td className="py-2 text-right text-gray-500">{fmt(row.totalServiceCharge)}</td>
                      <td className="py-2 text-right text-gray-400 text-xs">
                        {row.totalRevenue > 0
                          ? `${((row.totalServiceCharge / row.totalRevenue) * 100).toFixed(1)}%`
                          : '—'}
                      </td>
                      <td className="py-2 text-right">
                        <input
                          type="number"
                          min={0}
                          step={0.01}
                          placeholder="0.00"
                          value={row.billedAmount}
                          onChange={(e) => updateRow(idx, 'billedAmount', e.target.value)}
                          className="w-full border border-gray-200 rounded-lg px-2 py-1 text-sm text-right focus:outline-none focus:ring-2 focus:ring-gray-900"
                        />
                      </td>
                      <td className="py-2 pl-3">
                        <input
                          type="text"
                          placeholder="Observaciones…"
                          value={row.note}
                          onChange={(e) => updateRow(idx, 'note', e.target.value)}
                          className="w-full border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end pt-2">
              <Button onClick={() => guardar()} loading={saving}>
                Guardar corte
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Historial */}
      <div>
        <p className="text-sm font-semibold text-gray-700 mb-3">Historial de cortes</p>
        {historyLoading ? (
          <p className="text-sm text-gray-400">Cargando…</p>
        ) : history.length === 0 ? (
          <Card className="py-10 text-center">
            <p className="text-sm text-gray-400">Sin cortes registrados aún</p>
          </Card>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 text-xs text-gray-400 font-semibold uppercase tracking-wide">Negocio</th>
                  <th className="text-left py-2 text-xs text-gray-400 font-semibold uppercase tracking-wide w-40">Período</th>
                  <th className="text-right py-2 text-xs text-gray-400 font-semibold uppercase tracking-wide w-20">Órdenes</th>
                  <th className="text-right py-2 text-xs text-gray-400 font-semibold uppercase tracking-wide w-32">Total facturado</th>
                  <th className="text-right py-2 text-xs text-gray-400 font-semibold uppercase tracking-wide w-36">Cargo servicio</th>
                  <th className="text-right py-2 text-xs text-gray-400 font-semibold uppercase tracking-wide w-36">A facturar TEFFY</th>
                  <th className="text-left py-2 text-xs text-gray-400 font-semibold uppercase tracking-wide pl-3">Nota</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {history.map((rec) => (
                  <tr key={rec.id} className="hover:bg-gray-50">
                    <td className="py-2 font-medium text-gray-900">{rec.restaurant.name}</td>
                    <td className="py-2 text-gray-500 text-xs">
                      {new Date(rec.fromDate).toLocaleDateString('es-MX')} – {new Date(rec.toDate).toLocaleDateString('es-MX')}
                    </td>
                    <td className="py-2 text-right text-gray-600">{rec.orderCount}</td>
                    <td className="py-2 text-right text-gray-700">{fmt(Number(rec.totalRevenue))}</td>
                    <td className="py-2 text-right text-gray-500">{fmt(Number(rec.totalServiceCharge))}</td>
                    <td className="py-2 text-right font-semibold text-gray-900">
                      {rec.billedAmount != null ? fmt(Number(rec.billedAmount)) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="py-2 text-gray-400 text-xs pl-3">{rec.note ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Create modal ─────────────────────────────────────────────────────────────
function CreateRestaurantModal({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: () => void
}) {
  const [form, setForm] = useState({
    restaurantName: '',
    adminName: '',
    adminEmail: '',
    adminPassword: '',
    plan: PlanType.BASIC,
    planExpiresAt: '',
  })
  const [error, setError] = useState('')

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      const payload = {
        ...form,
        planExpiresAt: form.planExpiresAt ? new Date(form.planExpiresAt).toISOString() : undefined,
      }
      const { data } = await api.post('/superadmin/restaurants', payload)
      return data
    },
    onSuccess: () => onCreated(),
    onError: (e: { response?: { data?: { message?: string } } }) => {
      setError(e.response?.data?.message ?? 'Error al crear el negocio')
    },
  })

  const set = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [field]: e.target.value }))

  const canSubmit =
    form.restaurantName.length >= 2 &&
    form.adminName.length >= 2 &&
    form.adminEmail.includes('@') &&
    form.adminPassword.length >= 8

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
        <h2 className="text-xl font-bold text-gray-900">Nuevo negocio</h2>

        <Field label="Nombre del negocio">
          <Input value={form.restaurantName} onChange={set('restaurantName')} placeholder="Mi Restaurante" />
        </Field>

        <hr className="border-gray-100" />
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Administrador
        </p>

        <Field label="Nombre">
          <Input value={form.adminName} onChange={set('adminName')} placeholder="Juan Pérez" />
        </Field>
        <Field label="Email">
          <Input type="email" value={form.adminEmail} onChange={set('adminEmail')} placeholder="admin@negocio.com" />
        </Field>
        <Field label="Contraseña">
          <Input type="password" value={form.adminPassword} onChange={set('adminPassword')} placeholder="Mínimo 8 caracteres" />
        </Field>

        <hr className="border-gray-100" />
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Plan</p>

        <Field label="Plan">
          <select
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            value={form.plan}
            onChange={(e) => setForm((f) => ({ ...f, plan: e.target.value as PlanType }))}
          >
            <option value={PlanType.BASIC}>BASIC</option>
            <option value={PlanType.PRO}>PRO</option>
            <option value={PlanType.ENTERPRISE}>ENTERPRISE</option>
          </select>
        </Field>
        <Field label="Fecha de vencimiento (opcional)">
          <Input type="date" value={form.planExpiresAt} onChange={set('planExpiresAt')} />
        </Field>

        {error && <p className="text-sm text-red-500">{error}</p>}

        <div className="flex gap-2 pt-2">
          <Button variant="secondary" className="flex-1 justify-center" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            className="flex-1 justify-center"
            loading={isPending}
            disabled={!canSubmit}
            onClick={() => mutate()}
          >
            Crear negocio
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─── Plan modal ───────────────────────────────────────────────────────────────
function PlanModal({
  restaurant,
  onClose,
  onSaved,
}: {
  restaurant: RestaurantSummary
  onClose: () => void
  onSaved: () => void
}) {
  const [plan, setPlan] = useState<PlanType>(restaurant.plan)
  const [expiresAt, setExpiresAt] = useState(
    restaurant.planExpiresAt
      ? new Date(restaurant.planExpiresAt).toISOString().slice(0, 10)
      : ''
  )
  const [serviceChargePct, setServiceChargePct] = useState(restaurant.serviceChargePct ?? 1.5)
  const [error, setError] = useState('')

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      const payload = {
        plan,
        planExpiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        serviceChargePct,
      }
      const { data } = await api.patch(`/superadmin/restaurants/${restaurant.id}/plan`, payload)
      return data
    },
    onSuccess: () => onSaved(),
    onError: (e: { response?: { data?: { message?: string } } }) => {
      setError(e.response?.data?.message ?? 'Error al actualizar el plan')
    },
  })

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Cambiar plan</h2>
          <p className="text-sm text-gray-500">{restaurant.name}</p>
        </div>

        <Field label="Plan">
          <select
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            value={plan}
            onChange={(e) => setPlan(e.target.value as PlanType)}
          >
            <option value={PlanType.BASIC}>BASIC — Solo operación básica</option>
            <option value={PlanType.PRO}>PRO — + Lealtad, reservas, valet, impresión</option>
            <option value={PlanType.ENTERPRISE}>ENTERPRISE — + IA, cobro por habitación</option>
          </select>
        </Field>

        <Field label="Fecha de vencimiento (vacío = sin límite)">
          <Input
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
        </Field>

        <Field label="Comisión de plataforma (%)">
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={0}
              max={100}
              step={0.5}
              value={serviceChargePct}
              onChange={(e) => setServiceChargePct(Math.max(0, Number(e.target.value)))}
              className="w-24 border border-gray-200 rounded-lg px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
            <span className="text-sm text-gray-500">%</span>
            <span className="text-xs text-gray-400">(por defecto 1.5%)</span>
          </div>
        </Field>

        {error && <p className="text-sm text-red-500">{error}</p>}

        <div className="flex gap-2 pt-2">
          <Button variant="secondary" className="flex-1 justify-center" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            className="flex-1 justify-center"
            loading={isPending}
            onClick={() => mutate()}
          >
            Guardar
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─── Event Credits modal ──────────────────────────────────────────────────────
function EventCreditsModal({
  restaurant,
  onClose,
  onSaved,
}: {
  restaurant: RestaurantSummary
  onClose: () => void
  onSaved: () => void
}) {
  const [credits, setCredits] = useState<string>('1')
  const [error, setError] = useState('')

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      const { data } = await api.patch(`/superadmin/restaurants/${restaurant.id}/event-credits`, {
        credits: Number(credits),
      })
      return data
    },
    onSuccess: () => onSaved(),
    onError: (e: { response?: { data?: { message?: string } } }) => {
      setError(e.response?.data?.message ?? 'Error al actualizar créditos')
    },
  })

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Créditos de evento</h2>
          <p className="text-sm text-gray-500">{restaurant.name}</p>
        </div>
        <p className="text-sm text-gray-500">
          Cada crédito permite crear y activar 1 evento. Al activar un evento se descuenta 1 crédito automáticamente.
        </p>
        <Field label="Establecer créditos disponibles">
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={0}
              value={credits}
              onChange={(e) => setCredits(e.target.value)}
              className="w-24 border border-gray-200 rounded-lg px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
            <span className="text-sm text-gray-400">eventos</span>
          </div>
        </Field>
        {error && <p className="text-sm text-red-500">{error}</p>}
        <div className="flex gap-2 pt-2">
          <Button variant="secondary" className="flex-1 justify-center" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            className="flex-1 justify-center"
            loading={isPending}
            disabled={credits === '' || Number(credits) < 0}
            onClick={() => mutate()}
          >
            Guardar
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─── Payment modal ────────────────────────────────────────────────────────────
function PaymentModal({ restaurant, onClose }: { restaurant: RestaurantSummary; onClose: () => void }) {
  const [gateway, setGateway] = useState<'stripe' | ''>('')
  const [publicKey, setPublicKey] = useState('')
  const [secretKey, setSecretKey] = useState('')
  const [webhookSecret, setWebhookSecret] = useState('')
  const [testMode, setTestMode] = useState(false)
  const [enabled, setEnabled] = useState(false)
  const [error, setError] = useState('')

  type PaymentData = { paymentGateway: string | null; gatewayPublicKey: string; gatewaySecretKey: string; gatewayWebhookSecret: string; gatewayTestMode: boolean; paymentEnabled: boolean }

  const { data: paymentData, isLoading } = useQuery<PaymentData>({
    queryKey: ['payment-config', restaurant.id],
    queryFn: async () => {
      const { data } = await api.get(`/superadmin/restaurants/${restaurant.id}/payment`)
      return data as PaymentData
    },
    staleTime: 0,
  })

  useEffect(() => {
    if (!paymentData) return
    setGateway((paymentData.paymentGateway as 'stripe' | '') ?? '')
    setPublicKey(paymentData.gatewayPublicKey ?? '')
    setSecretKey(paymentData.gatewaySecretKey ?? '')
    setWebhookSecret(paymentData.gatewayWebhookSecret ?? '')
    setTestMode(paymentData.gatewayTestMode)
    setEnabled(paymentData.paymentEnabled)
  }, [paymentData])

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      await api.patch(`/superadmin/restaurants/${restaurant.id}/payment`, {
        paymentGateway: gateway || null,
        gatewayPublicKey: publicKey,
        gatewaySecretKey: secretKey,
        gatewayWebhookSecret: webhookSecret,
        gatewayTestMode: testMode,
        paymentEnabled: enabled,
      })
    },
    onSuccess: () => onClose(),
    onError: (e: { response?: { data?: { message?: string } } }) => {
      setError(e.response?.data?.message ?? 'Error al guardar')
    },
  })

  const FIELDS = [
    { label: 'Publishable key', value: publicKey, onChange: setPublicKey, hint: 'pk_live_...', secret: false },
    { label: 'Secret key',      value: secretKey, onChange: setSecretKey, hint: 'sk_live_...', secret: true  },
    { label: 'Webhook secret',  value: webhookSecret, onChange: setWebhookSecret, hint: 'whsec_...', secret: true },
  ]

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Pasarela de pago</h2>
          <p className="text-sm text-gray-500">{restaurant.name}</p>
        </div>

        {isLoading ? (
          <p className="text-sm text-gray-400 py-4 text-center">Cargando...</p>
        ) : (
          <>
            {/* Gateway selector */}
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-2">Pasarela</p>
              <div className="flex gap-2">
                {([['', 'Sin pasarela'], ['stripe', 'Stripe']] as const).map(([id, label]) => (
                  <button
                    key={id}
                    onClick={() => setGateway(id === gateway ? gateway : id as 'stripe' | '')}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-all ${gateway === id ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {gateway && (
              <div className="flex flex-col gap-3 bg-gray-50 rounded-xl p-4">
                {FIELDS.map(({ label, value, onChange, hint, secret }) => (
                  <div key={label}>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
                    <div className="relative">
                      <input
                        type={secret ? 'password' : 'text'}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={value === '***' ? '(guardado — escribe para reemplazar)' : hint}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-gray-400 bg-white"
                      />
                      {value === '***' && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-emerald-600 font-semibold">✓ guardado</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {gateway && (
              <div className="flex flex-col gap-3">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={testMode}
                    onClick={() => setTestMode((v) => !v)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${testMode ? 'bg-amber-400' : 'bg-gray-200'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${testMode ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                  <span className="text-sm text-gray-600">
                    Modo prueba (sandbox)
                    {testMode && <span className="ml-2 text-xs font-semibold text-amber-600">· Los cobros no son reales</span>}
                  </span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={enabled}
                    onClick={() => setEnabled((v) => !v)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? 'bg-emerald-500' : 'bg-gray-200'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                  <span className="text-sm text-gray-700 font-medium">Habilitar pago digital en la app</span>
                </label>
              </div>
            )}

            {error && <p className="text-sm text-red-500">{error}</p>}

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg">Cancelar</button>
              <button
                onClick={() => mutate()}
                disabled={isPending}
                className="px-4 py-2 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50"
              >
                {isPending ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ─── Webhook modal ────────────────────────────────────────────────────────────
function WebhookModal({
  restaurant,
  onClose,
}: {
  restaurant: RestaurantSummary
  onClose: () => void
}) {
  const [enabled, setEnabled] = useState(false)
  const [url, setUrl] = useState('')
  const [secret, setSecret] = useState('')
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState('')

  const { isLoading } = useQuery({
    queryKey: ['webhook-config', restaurant.id],
    queryFn: async () => {
      const { data } = await api.get(`/superadmin/restaurants/${restaurant.id}/webhook`)
      return data as { webhookEnabled: boolean; webhookUrl: string | null; webhookSecret: string | null }
    },
    onSuccess: (data: { webhookEnabled: boolean; webhookUrl: string | null; webhookSecret: string | null }) => {
      setEnabled(data.webhookEnabled)
      setUrl(data.webhookUrl ?? '')
      setSecret(data.webhookSecret ?? '')
      setLoaded(true)
    },
  } as Parameters<typeof useQuery>[0])

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      const { data } = await api.patch(`/superadmin/restaurants/${restaurant.id}/webhook`, {
        webhookEnabled: enabled,
        webhookUrl: url || null,
        webhookSecret: secret || null,
      })
      return data
    },
    onSuccess: () => onClose(),
    onError: (e: { response?: { data?: { message?: string } } }) => {
      setError(e.response?.data?.message ?? 'Error al guardar')
    },
  })

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Webhook</h2>
          <p className="text-sm text-gray-500">{restaurant.name}</p>
        </div>

        {isLoading && !loaded ? (
          <p className="text-sm text-gray-400 py-4 text-center">Cargando...</p>
        ) : (
          <>
            <label className="flex items-center justify-between gap-3 cursor-pointer">
              <div>
                <p className="text-sm font-medium text-gray-900">Webhook habilitado</p>
                <p className="text-xs text-gray-500">Envía eventos a tu sistema cuando cambia una orden</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={enabled}
                onClick={() => setEnabled((v) => !v)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  enabled ? 'bg-gray-900' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </label>

            <Field label="URL del endpoint">
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://tu-sistema.com/webhook/teffy"
              />
            </Field>

            <Field label="Secreto (para verificar firma HMAC-SHA256)">
              <Input
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                placeholder="Clave secreta opcional"
              />
            </Field>

            <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-500 space-y-1">
              <p className="font-semibold text-gray-700">Eventos que se envían:</p>
              <p>• <code>order.confirmed</code> — orden confirmada por el mesero</p>
              <p>• <code>order.paid</code> — orden pagada</p>
              <p>• <code>order.cancelled</code> — orden cancelada</p>
              <p className="pt-1">Cada llamada incluye el header <code>X-Teffy-Signature: sha256=...</code> si configuraste un secreto.</p>
            </div>
          </>
        )}

        {error && <p className="text-sm text-red-500">{error}</p>}

        <div className="flex gap-2 pt-2">
          <Button variant="secondary" className="flex-1 justify-center" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            className="flex-1 justify-center"
            loading={isPending}
            disabled={!loaded}
            onClick={() => mutate()}
          >
            Guardar
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─── Venue modal ──────────────────────────────────────────────────────────────
function VenueModal({
  restaurant,
  onClose,
  onSaved,
}: {
  restaurant: RestaurantSummary
  onClose: () => void
  onSaved: () => void
}) {
  const [venueType, setVenueType] = useState<VenueType>(restaurant.venueType)
  const [selfService, setSelfService] = useState(restaurant.selfService)
  const [skipKitchen, setSkipKitchen] = useState(restaurant.skipKitchen)
  const [hidePrices, setHidePrices] = useState(restaurant.hidePrices)
  const [error, setError] = useState('')

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      const payload = { venueType, selfService, skipKitchen, hidePrices }
      const { data } = await api.patch(`/superadmin/restaurants/${restaurant.id}/venue`, payload)
      return data
    },
    onSuccess: () => onSaved(),
    onError: (e: { response?: { data?: { message?: string } } }) => {
      setError(e.response?.data?.message ?? 'Error al actualizar')
    },
  })

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Tipo de establecimiento</h2>
          <p className="text-sm text-gray-500">{restaurant.name}</p>
        </div>

        <Field label="Tipo de establecimiento">
          <select
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            value={venueType}
            onChange={(e) => setVenueType(e.target.value as VenueType)}
          >
            <option value={VenueType.RESTAURANT}>Restaurante</option>
            <option value={VenueType.HOTEL}>Hotel</option>
            <option value={VenueType.APARTMENT}>Apartamento</option>
            <option value={VenueType.HOSPITAL}>Hospital</option>
            <option value={VenueType.EVENT}>Salón de eventos</option>
          </select>
        </Field>

        <div className="space-y-3 pt-1">
          <label className="flex items-center justify-between gap-3 cursor-pointer">
            <div>
              <p className="text-sm font-medium text-gray-900">Self-service</p>
              <p className="text-xs text-gray-500">Los clientes ordenan sin mesero</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={selfService}
              onClick={() => setSelfService((v) => !v)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                selfService ? 'bg-gray-900' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  selfService ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </label>

          <label className="flex items-center justify-between gap-3 cursor-pointer">
            <div>
              <p className="text-sm font-medium text-gray-900">Sin cocina</p>
              <p className="text-xs text-gray-500">El mesero sirve directo sin pasar por cocina</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={skipKitchen}
              onClick={() => setSkipKitchen((v) => !v)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                skipKitchen ? 'bg-gray-900' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  skipKitchen ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </label>

          <label className="flex items-center justify-between gap-3 cursor-pointer">
            <div>
              <p className="text-sm font-medium text-gray-900">Ocultar precios</p>
              <p className="text-xs text-gray-500">El comensal no verá precios en el menú ni al pagar</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={hidePrices}
              onClick={() => setHidePrices((v) => !v)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                hidePrices ? 'bg-gray-900' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  hidePrices ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </label>
        </div>

        {error && <p className="text-sm text-red-500">{error}</p>}

        <div className="flex gap-2 pt-2">
          <Button variant="secondary" className="flex-1 justify-center" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            className="flex-1 justify-center"
            loading={isPending}
            onClick={() => mutate()}
          >
            Guardar
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─── Panel Grupos Corporativos ────────────────────────────────────────────────

interface RestaurantGroup {
  id: string
  name: string
  createdAt: string
  restaurants: { id: string; name: string; logoUrl: string | null; isActive: boolean }[]
  users: { id: string; name: string; email: string }[]
}

function GroupsPanel() {
  const queryClient = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState<RestaurantGroup | null>(null)
  const [newGroupName, setNewGroupName] = useState('')
  const [createError, setCreateError] = useState('')
  const [listError, setListError] = useState('')

  const { data: groups = [], isLoading } = useQuery<RestaurantGroup[]>({
    queryKey: ['superadmin-groups'],
    queryFn: async () => {
      const { data } = await api.get('/superadmin/groups')
      return data
    },
    throwOnError: false,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    meta: { onError: (e: any) => setListError(e?.response?.data?.message ?? 'Error al cargar grupos') },
  })

  const { mutate: createGroup, isPending: creating } = useMutation({
    mutationFn: async (name: string) => { await api.post('/superadmin/groups', { name }) },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-groups'] })
      setShowCreate(false)
      setNewGroupName('')
      setCreateError('')
    },
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Error al crear grupo. Verifica que el API esté actualizado.'
      setCreateError(msg)
    },
  })

  const { mutate: deleteGroup } = useMutation({
    mutationFn: async (id: string) => { await api.delete(`/superadmin/groups/${id}`) },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['superadmin-groups'] }); setSelectedGroup(null) },
    onError: () => alert('Error al eliminar el grupo'),
  })

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-black text-gray-900">Grupos Corporativos</h2>
        <Button size="sm" onClick={() => { setShowCreate(true); setCreateError('') }}>+ Nuevo grupo</Button>
      </div>

      {listError && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
          {listError} — El API puede estar desplegándose, espera un momento y recarga.
        </div>
      )}

      {showCreate && (
        <Card className="p-4 space-y-3 border-2 border-dashed border-gray-300">
          <Field label="Nombre del grupo">
            <Input
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="Grupo Restaurantes XYZ"
              onKeyDown={(e) => { if (e.key === 'Enter' && newGroupName.trim()) createGroup(newGroupName.trim()) }}
            />
          </Field>
          {createError && <p className="text-xs text-red-500">{createError}</p>}
          <div className="flex gap-2">
            <Button size="sm" loading={creating} onClick={() => { if (newGroupName.trim()) createGroup(newGroupName.trim()) }}>Crear</Button>
            <Button size="sm" variant="ghost" onClick={() => { setShowCreate(false); setNewGroupName(''); setCreateError('') }}>Cancelar</Button>
          </div>
        </Card>
      )}

      {isLoading && <p className="text-gray-400 text-sm">Cargando grupos...</p>}

      <div className="space-y-4">
        {groups.map((g) => (
          <Card key={g.id} className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-900">{g.name}</h3>
                <p className="text-xs text-gray-400">{g.restaurants.length} sucursales · {g.users.length} usuario(s) corporativo(s)</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" onClick={() => setSelectedGroup(g)}>Gestionar</Button>
                <Button
                  size="sm" variant="ghost"
                  className="text-red-500 hover:text-red-700"
                  onClick={() => { if (confirm(`¿Eliminar grupo "${g.name}"?`)) deleteGroup(g.id) }}
                >
                  Eliminar
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {g.restaurants.map((r) => (
                <span key={r.id} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">{r.name}</span>
              ))}
              {g.restaurants.length === 0 && <span className="text-xs text-gray-400 italic">Sin sucursales asignadas</span>}
            </div>
            {g.users.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {g.users.map((u) => (
                  <span key={u.id} className="text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded-full">👤 {u.name} — {u.email}</span>
                ))}
              </div>
            )}
          </Card>
        ))}
        {!isLoading && groups.length === 0 && (
          <p className="text-gray-400 text-sm text-center py-8">No hay grupos corporativos. Crea uno con el botón de arriba.</p>
        )}
      </div>

      {selectedGroup && (
        <GroupDetailModal
          group={selectedGroup}
          onClose={() => setSelectedGroup(null)}
          onRefresh={() => { queryClient.invalidateQueries({ queryKey: ['superadmin-groups'] }); setSelectedGroup(null) }}
        />
      )}
    </div>
  )
}

function GroupDetailModal({
  group,
  onClose,
  onRefresh,
}: {
  group: RestaurantGroup
  onClose: () => void
  onRefresh: () => void
}) {
  const { data: allRestaurants = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ['superadmin-restaurants-list'],
    queryFn: async () => {
      const { data } = await api.get('/superadmin/restaurants')
      return data.map((r: { id: string; name: string }) => ({ id: r.id, name: r.name }))
    },
  })

  const [selectedRestaurantIds, setSelectedRestaurantIds] = useState<string[]>(
    group.restaurants.map((r) => r.id)
  )
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '' })
  const [userError, setUserError] = useState('')

  const { mutate: saveRestaurants, isPending: savingRests } = useMutation({
    mutationFn: async () => {
      await api.patch(`/superadmin/groups/${group.id}/restaurants`, { restaurantIds: selectedRestaurantIds })
    },
    onSuccess: () => { onRefresh(); alert('Sucursales actualizadas') },
  })

  const { mutate: createUser, isPending: creatingUser } = useMutation({
    mutationFn: async () => { await api.post(`/superadmin/groups/${group.id}/users`, newUser) },
    onSuccess: () => {
      onRefresh()
      setNewUser({ name: '', email: '', password: '' })
      setUserError('')
      alert('Usuario corporativo creado')
    },
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Error al crear usuario'
      setUserError(msg)
    },
  })

  const { mutate: deleteUser } = useMutation({
    mutationFn: async (userId: string) => { await api.delete(`/superadmin/groups/${group.id}/users/${userId}`) },
    onSuccess: onRefresh,
  })

  const toggleRestaurant = (id: string) =>
    setSelectedRestaurantIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-black text-gray-900">Gestionar: {group.name}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-3">Sucursales del grupo</p>
            <div className="space-y-1 max-h-48 overflow-y-auto border border-gray-100 rounded-xl p-3">
              {allRestaurants.map((r) => (
                <label key={r.id} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 rounded-lg px-2 py-1">
                  <input
                    type="checkbox"
                    checked={selectedRestaurantIds.includes(r.id)}
                    onChange={() => toggleRestaurant(r.id)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-gray-700">{r.name}</span>
                </label>
              ))}
            </div>
            <Button size="sm" loading={savingRests} onClick={() => saveRestaurants()} className="mt-3">
              Guardar sucursales
            </Button>
          </div>

          <div>
            <p className="text-sm font-semibold text-gray-700 mb-3">Usuarios corporativos</p>
            {group.users.length > 0 && (
              <div className="space-y-2 mb-4">
                {group.users.map((u) => (
                  <div key={u.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                    <div>
                      <p className="text-sm font-medium text-gray-800">{u.name}</p>
                      <p className="text-xs text-gray-500">{u.email}</p>
                    </div>
                    <button
                      onClick={() => { if (confirm('¿Eliminar usuario?')) deleteUser(u.id) }}
                      className="text-red-400 hover:text-red-600 text-xs font-medium"
                    >
                      Eliminar
                    </button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-gray-500 font-semibold mb-2 mt-1">Crear nuevo usuario</p>
            <div className="space-y-2">
              <Input placeholder="Nombre completo" value={newUser.name} onChange={(e) => setNewUser((u) => ({ ...u, name: e.target.value }))} />
              <Input placeholder="Email" type="email" value={newUser.email} onChange={(e) => setNewUser((u) => ({ ...u, email: e.target.value }))} />
              <Input placeholder="Contraseña (mín. 8 caracteres)" type="password" value={newUser.password} onChange={(e) => setNewUser((u) => ({ ...u, password: e.target.value }))} />
              {userError && <p className="text-xs text-red-500">{userError}</p>}
              <Button
                size="sm"
                loading={creatingUser}
                onClick={() => {
                  if (!newUser.name || !newUser.email || newUser.password.length < 8) {
                    setUserError('Completa todos los campos (contraseña mínimo 8 caracteres)')
                    return
                  }
                  createUser()
                }}
              >
                Crear usuario corporativo
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
